import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import type { VanityLink, ClickStats } from "../../api/_lib/types";

interface Props {
  link: VanityLink | null;
  onClose: () => void;
}

export function LinkStats({ link, onClose }: Props) {
  const [stats, setStats] = useState<ClickStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!link) { setStats(null); return; }
    setLoading(true);
    api.stats.get(link.slug)
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [link?.slug]);

  const dailyData = stats
    ? Object.entries(stats.daily)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-30)
        .map(([date, count]) => ({ date: date.slice(5), count }))
    : [];

  const referrerData = stats
    ? Object.entries(stats.referrers)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([domain, count]) => ({ domain: domain.replace(/,/g, "."), count }))
    : [];

  const countryData = stats
    ? Object.entries(stats.countries)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
        .map(([country, count]) => ({ country, count }))
    : [];

  return (
    <Dialog open={!!link} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">/{link?.slug}</DialogTitle>
          <p className="text-sm text-muted-foreground truncate">{link?.url}</p>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0">
        {loading && (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading stats…</div>
        )}

        {!loading && stats && (
          <div className="space-y-8 pt-2">
            <StatCard label="Total clicks" value={link?.totalClicks ?? 0} />

            {dailyData.length > 0 && (
              <Section title="Daily clicks (last 30 days)">
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={dailyData}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Line type="monotone" dataKey="count" stroke="currentColor" strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Section>
            )}

            {referrerData.length > 0 && (
              <Section title="Top referrers">
                <ResponsiveContainer width="100%" height={Math.max(100, referrerData.length * 28)}>
                  <BarChart data={referrerData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis dataKey="domain" type="category" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={90} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Bar dataKey="count" fill="currentColor" radius={2} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            )}

            {countryData.length > 0 && (
              <Section title="Countries">
                <ResponsiveContainer width="100%" height={Math.max(100, countryData.length * 28)}>
                  <BarChart data={countryData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis dataKey="country" type="category" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Bar dataKey="count" fill="currentColor" radius={2} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            )}

            {dailyData.length === 0 && referrerData.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-8">No click data yet.</p>
            )}
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-3xl font-semibold mt-1 tabular-nums">{value.toLocaleString()}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
      {children}
    </div>
  );
}
